// Generates a 9:16 Story-ready PNG with the bag tinted to the selected color,
// then either shares it via Web Share API (with files) or downloads it.

const BAG_IMAGE =
  "https://customer-assets.emergentagent.com/job_e541debc-b530-4ffd-aa48-47273fe77542/artifacts/3g7xwvdl_KSBLUE.jpg";

// Source sprite is a 5x4 grid; top-left cell = Klein Blue.
// Image natural size is roughly 2000 x 1600. We crop the top-left cell.
async function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

export async function generateShareImage(color) {
  const W = 1080;
  const H = 1920;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background: Klein-blue band on top, white below (editorial)
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#002FA7";
  ctx.fillRect(0, 0, W, 360);

  // Header: KISUBIKA wordmark + "AR" superscript
  ctx.fillStyle = "#FFFFFF";
  ctx.textBaseline = "middle";
  ctx.font = '600 64px "Hanken Grotesk", "Helvetica Neue", sans-serif';
  ctx.fillText("KISUBIKA", 80, 180);
  ctx.font = '500 22px "Manrope", system-ui, sans-serif';
  ctx.fillText("AR PREVIEW", 80, 240);

  // Right side header line
  ctx.font = '500 20px "Manrope", system-ui, sans-serif';
  ctx.textAlign = "right";
  ctx.fillText("LDN · 2026", W - 80, 180);
  ctx.fillText("VOL. 01 · TOTE NO. 1", W - 80, 240);
  ctx.textAlign = "left";

  // Bag region — cropped from sprite top-left cell, tinted
  const img = await loadImage(BAG_IMAGE);
  // The sprite has 5 columns and 4 rows. Each cell ~ naturalWidth/5 x naturalHeight/4
  const cellW = img.naturalWidth / 5;
  const cellH = img.naturalHeight / 4;

  // Draw bag offscreen (tinted)
  const off = document.createElement("canvas");
  off.width = cellW;
  off.height = cellH;
  const offCtx = off.getContext("2d");
  offCtx.filter = color.filter && color.filter !== "none" ? color.filter : "none";
  offCtx.drawImage(img, 0, 0, cellW, cellH, 0, 0, cellW, cellH);

  // Sample the filtered "white" from a top corner pixel — that's what the
  // image's white background becomes after the CSS filter is applied. We use
  // it as the bag panel background so the filter-darkened cell blends in
  // and the bag floats cleanly without a visible grey rectangle.
  const sample = offCtx.getImageData(8, 8, 1, 1).data;
  const panelBg = `rgb(${sample[0]}, ${sample[1]}, ${sample[2]})`;

  // Bag area panel (matches the filter-shifted white so cell edges disappear)
  const panelTop = 360;
  const panelBottom = 1500;
  ctx.fillStyle = panelBg;
  ctx.fillRect(0, panelTop, W, panelBottom - panelTop);

  // Floor shadow
  ctx.save();
  ctx.translate(W / 2, 1380);
  const grad = ctx.createRadialGradient(0, 0, 10, 0, 0, 320);
  grad.addColorStop(0, "rgba(10,10,10,0.18)");
  grad.addColorStop(1, "rgba(10,10,10,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.ellipse(0, 0, 360, 26, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Bag image — fit comfortably between header (360) and color label (~1500)
  const targetH = 980;
  const ratio = cellW / cellH;
  const targetW = targetH * ratio;
  const bagX = (W - targetW) / 2;
  const bagY = 420;
  ctx.drawImage(off, bagX, bagY, targetW, targetH);

  // Color name + index in the bottom panel
  ctx.fillStyle = "#0A0A0A";
  ctx.font = '600 84px "Hanken Grotesk", "Helvetica Neue", sans-serif';
  ctx.textAlign = "center";
  ctx.fillText(color.name, W / 2, 1620);

  ctx.font = '500 24px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = "#4A4A4A";
  ctx.fillText(
    `№ ${String(color.index || 1).padStart(2, "0")} OF 20  ·  ${color.hex.toUpperCase()}`,
    W / 2,
    1690
  );

  // Hex chip
  ctx.fillStyle = color.hex;
  ctx.fillRect(W / 2 - 30, 1730, 60, 60);
  ctx.strokeStyle = "#0A0A0A";
  ctx.lineWidth = 2;
  ctx.strokeRect(W / 2 - 30, 1730, 60, 60);

  // CTA bar
  ctx.fillStyle = "#002FA7";
  ctx.fillRect(0, H - 110, W, 110);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = '600 30px "Manrope", system-ui, sans-serif';
  const cta = "TRY YOURS  ·  KISUBIKA.SHOP/AR";
  ctx.fillText(cta, W / 2, H - 55);
  ctx.textAlign = "left";

  // Convert to blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png", 0.92);
  });
}

export async function shareOrDownload(color) {
  const blob = await generateShareImage(color);
  if (!blob) throw new Error("Could not generate image");

  const file = new File(
    [blob],
    `kisubika-${color.id}.png`,
    { type: "image/png" }
  );

  // Try Web Share with files (iOS/Android system share sheet → IG, WA, etc.)
  if (
    navigator.canShare &&
    navigator.canShare({ files: [file] }) &&
    navigator.share
  ) {
    try {
      await navigator.share({
        files: [file],
        title: `KISUBIKA · ${color.name}`,
        text: `My KISUBIKA tote in ${color.name}. See it in AR at kisubika.shop/ar`,
      });
      return { method: "shared" };
    } catch (e) {
      if (e?.name === "AbortError") return { method: "cancelled" };
      // fall through to download
    }
  }

  // Fallback: download
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { method: "downloaded" };
}
